import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  createDepartmentSchema,
  type CreateDepartmentInput,
  type Department,
  type ApiError,
} from '@pms/shared';
import { departmentsService } from '@/services/departments.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable, type Column } from '@/components/ui/data-table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FormState {
  open: boolean;
  editing: Department | null;
}

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

export function DepartmentsSection() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<FormState>({ open: false, editing: null });
  const [pendingDeactivate, setPendingDeactivate] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['departments', { page, q: search }],
    queryFn: () => departmentsService.list({ page, limit: 20, q: search || undefined }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => departmentsService.deactivate(id),
    onSuccess: () => {
      toast.success('Department deactivated');
      void queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const columns: Column<Department>[] = [
    { key: 'code', header: 'Code', cell: (d) => <span className="font-mono text-xs">{d.code}</span> },
    { key: 'name', header: 'Name', cell: (d) => <span className="font-medium">{d.name}</span> },
    {
      key: 'is_active',
      header: 'Status',
      cell: (d) => (
        <span
          className={
            d.is_active
              ? 'inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700'
              : 'inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'
          }
        >
          {d.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24 text-right',
      cell: (d) => (
        <div className="flex justify-end gap-1">
          <Button size="icon" variant="ghost" onClick={() => setForm({ open: true, editing: d })} aria-label="Edit">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setPendingDeactivate({ id: d.id, name: d.name })}
            disabled={!d.is_active}
            aria-label="Deactivate"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Input
          placeholder="Search by code or name…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="max-w-sm"
        />
        <Button onClick={() => setForm({ open: true, editing: null })}>
          <Plus className="h-4 w-4" /> Add Department
        </Button>
      </div>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(d) => d.id}
        loading={isLoading}
        empty="No departments yet"
        pagination={{
          page,
          totalPages: data?.pagination.totalPages ?? 1,
          onPageChange: setPage,
        }}
      />

      <DepartmentFormDialog
        state={form}
        onClose={() => setForm({ open: false, editing: null })}
        onSaved={() => {
          setForm({ open: false, editing: null });
          void queryClient.invalidateQueries({ queryKey: ['departments'] });
        }}
      />

      <ConfirmDialog
        open={!!pendingDeactivate}
        onOpenChange={(o) => { if (!o) setPendingDeactivate(null); }}
        title={`Deactivate "${pendingDeactivate?.name}"?`}
        description="The department will be marked inactive and hidden from new assignments."
        confirmLabel="Deactivate"
        onConfirm={() => { if (pendingDeactivate) deactivateMutation.mutate(pendingDeactivate.id); }}
      />
    </div>
  );
}

function DepartmentFormDialog({
  state,
  onClose,
  onSaved,
}: {
  state: FormState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = state.editing;
  const { register, handleSubmit, reset, formState } = useForm<CreateDepartmentInput>({
    resolver: zodResolver(createDepartmentSchema),
    defaultValues: { code: '', name: '' },
    values: editing ? { code: editing.code, name: editing.name } : { code: '', name: '' },
  });

  const mutation = useMutation({
    mutationFn: async (input: CreateDepartmentInput) => {
      if (editing) {
        await departmentsService.update(editing.id, input);
      } else {
        await departmentsService.create(input);
      }
    },
    onSuccess: () => {
      toast.success(editing ? 'Department updated' : 'Department created');
      reset();
      onSaved();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog
      open={state.open}
      onOpenChange={(open) => {
        if (!open) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Department' : 'New Department'}</DialogTitle>
          <DialogDescription>Departments group staff under one umbrella.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="code">Code</Label>
            <Input id="code" placeholder="e.g. ENG" {...register('code')} disabled={!!editing} />
            {formState.errors.code && (
              <p className="text-xs text-destructive">{formState.errors.code.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input id="name" placeholder="e.g. Engineering" {...register('name')} />
            {formState.errors.name && (
              <p className="text-xs text-destructive">{formState.errors.name.message}</p>
            )}
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
